import React, { useMemo, useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface StarryBackgroundProps {
  placement?: 'fixed' | 'absolute';
  density?: 'minimal' | 'normal' | 'dense';
  animated?: boolean;
  className?: string;
}

const densitySettings: Record<'minimal' | 'normal' | 'dense', { base: number; optimized: number; mobile: number; static: number }> = {
  minimal: { base: 40, optimized: 25, mobile: 30, static: 18 },
  normal: { base: 80, optimized: 40, mobile: 55, static: 32 },
  dense: { base: 120, optimized: 70, mobile: 90, static: 48 }
};

const StarryBackground: React.FC<StarryBackgroundProps> = ({
  placement = 'fixed',
  density = 'normal',
  animated = true,
  className = ''
}) => {
  const shouldReduceMotion = useReducedMotion();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  // Handle window resize for responsiveness
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 200);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Determine star count based on screen width and reduced motion preference
  const stars = useMemo(() => {
    // Reduce animations on smaller devices or if user prefers reduced motion
    const isMobile = windowWidth < 768;
    const shouldAnimate = animated && !shouldReduceMotion;
    const shouldOptimize = isMobile || shouldReduceMotion || !animated;

    const settings = densitySettings[density] ?? densitySettings.normal;

    const baseCount = settings.base;
    const optimizedCount = settings.optimized;
    const mobileCount = settings.mobile;
    const staticCount = settings.static;

    // Adjust count based on device and preferences
    const animatedCount = shouldOptimize ? optimizedCount : (isMobile ? mobileCount : baseCount);
    const starCount = shouldAnimate ? animatedCount : staticCount;

    return Array.from({ length: starCount }).map(() => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      duration: shouldAnimate ? (shouldOptimize ? Math.random() * 2 + 3 : Math.random() * 3 + 2) : undefined, // Slower animations for performance
      delay: shouldAnimate ? Math.random() * 2 : undefined,
    }));
  }, [windowWidth, shouldReduceMotion, animated, density]);

  const isAnimated = animated && !shouldReduceMotion;
  const rocketConfigs = useMemo(
    () => [
      {
        duration: 14 + Math.random() * 4,
        startY: 15 + Math.random() * 10,
        delay: 0,
        direction: 'ltr' as const, // left to right
        startX: '-15%'
      },
      {
        duration: 16 + Math.random() * 5,
        startY: 40 + Math.random() * 15,
        delay: 8,
        direction: 'rtl' as const, // right to left
        startX: '115%'
      },
      {
        duration: 18 + Math.random() * 6,
        startY: 70 + Math.random() * 10,
        delay: 4,
        direction: 'ltr' as const,
        startX: '-15%'
      }
    ],
    []
  );

  return (
    <div
      className={`${placement === 'fixed' ? 'fixed' : 'absolute'} inset-0 z-0 overflow-hidden ${className}`.trim()}
      style={{ pointerEvents: "none" }}
    >
      {stars.map((star, index) =>
        isAnimated ? (
          <motion.div
            key={index}
            className="absolute rounded-full bg-white"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: star.size,
              height: star.size,
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
              translateZ: 0,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: star.duration,
              repeat: Infinity,
              delay: star.delay,
              ease: "easeInOut",
            }}
          />
        ) : (
          <div
            key={index}
            className="absolute rounded-full bg-white/70"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: star.size,
              height: star.size,
              opacity: 0.3 + Math.random() * 0.4,
            }}
          />
        )
      )}

      {isAnimated && placement === 'absolute' && rocketConfigs.map((config, index) => (
        <motion.div
          key={index}
          className="absolute"
          style={{
            top: `${config.startY}%`,
            left: config.startX,
            width: '50px',
            height: '25px'
          }}
          initial={{
            scaleX: config.direction === 'rtl' ? -1 : 1
          }}
          animate={{
            x: config.direction === 'ltr' ? ['0vw', '120vw'] : ['0vw', '-120vw'],
            y: [0, -8, 8, 0],
            scaleX: config.direction === 'rtl' ? -1 : 1
          }}
          transition={{
            duration: config.duration,
            repeat: Infinity,
            delay: config.delay,
            ease: "linear",
            scaleX: { duration: 0 }
          }}
        >
          <svg
            width="50"
            height="25"
            viewBox="0 0 100 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-[0_8px_16px_rgba(239,151,76,0.5)]"
          >
          <motion.path
            d="M10 25 Q15 20, 20 25 Q15 30, 10 25"
            fill="#F59E0B"
            animate={{
              d: [
                'M10 25 Q15 20, 20 25 Q15 30, 10 25',
                'M5 25 Q12 22, 18 25 Q12 28, 5 25',
                'M10 25 Q15 20, 20 25 Q15 30, 10 25'
              ],
              opacity: [0.8, 1, 0.8]
            }}
            transition={{ duration: 0.32, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.path
            d="M15 25 Q20 22, 25 25 Q20 28, 15 25"
            fill="#EF4444"
            animate={{
              d: [
                'M15 25 Q20 22, 25 25 Q20 28, 15 25',
                'M12 25 Q18 23, 23 25 Q18 27, 12 25',
                'M15 25 Q20 22, 25 25 Q20 28, 15 25'
              ],
              opacity: [0.9, 1, 0.9]
            }}
            transition={{ duration: 0.32, delay: 0.05, repeat: Infinity, ease: 'easeInOut' }}
          />
          <ellipse cx="50" cy="25" rx="20" ry="12" fill="#4B5563" />
          <path d="M35 30 L30 40 L35 35 Z" fill="#6B7280" />
          <path d="M35 20 L30 10 L35 15 Z" fill="#6B7280" />
          <circle cx="55" cy="25" r="5" fill="#1F2937" opacity="0.6" />
        </svg>
        </motion.div>
      ))}
    </div>
  );
};

export default StarryBackground;
